import React from 'react';
import styled, { css } from 'styled-components';

interface CardProps {
  children: React.ReactNode;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  hover?: boolean;
  gradient?: boolean;
  glass?: boolean;
  glow?: boolean;
  onClick?: () => void;
  className?: string;
  style?: React.CSSProperties;
  'data-testid'?: string;
}

const paddingMap = {
  none: '0',
  sm: '16px',
  md: '24px',
  lg: '32px',
};

const mobilePaddingMap = {
  none: '0',
  sm: '12px',
  md: '16px',
  lg: '20px',
};

const StyledCard = styled.div<{
  $padding: string;
  $mobilePadding: string;
  $hover: boolean;
  $clickable: boolean;
  $gradient: boolean;
  $glass: boolean;
  $glow: boolean;
}>`
  background: linear-gradient(145deg, 
    rgba(255, 255, 255, 1) 0%, 
    rgba(255, 252, 248, 1) 50%,
    rgba(255, 250, 245, 1) 100%
  );
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  transition: all ${({ theme }) => theme.transitions.normal};
  padding: ${({ $padding }) => $padding};
  position: relative;
  overflow: hidden;
  
  /* Subtle top border accent */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, 
      rgba(255, 107, 53, 0.6) 0%, 
      rgba(255, 140, 66, 0.4) 25%,
      rgba(255, 179, 71, 0.3) 50%, 
      rgba(212, 175, 55, 0.2) 75%,
      transparent 100%
    );
    border-radius: ${({ theme }) => theme.borderRadius.lg} ${({ theme }) => theme.borderRadius.lg} 0 0;
  }
  
  /* Corner accent glow */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 100px;
    height: 100px;
    background: radial-gradient(circle at 100% 0%, rgba(255, 179, 71, 0.08) 0%, transparent 70%);
    pointer-events: none;
  }
  
  @media (max-width: 768px) {
    padding: ${({ $mobilePadding }) => $mobilePadding};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    
    &::after {
      width: 60px;
      height: 60px;
    }
  }
  
  @media (max-width: 480px) {
    border-radius: ${({ theme }) => theme.borderRadius.sm};
  }
  
  ${({ $glass, theme }) => $glass && css`
    background: ${theme.glass.background};
    backdrop-filter: ${theme.glass.backdropBlur};
    -webkit-backdrop-filter: ${theme.glass.backdropBlur};
    border: ${theme.glass.border};
  `}
  
  ${({ $gradient }) => $gradient && css`
    background: linear-gradient(145deg, 
      rgba(255, 255, 255, 1) 0%, 
      rgba(255, 247, 237, 0.8) 50%,
      rgba(255, 243, 230, 0.6) 100%
    );
    border-color: rgba(255, 179, 71, 0.3);
    
    &::before {
      height: 4px;
      background: linear-gradient(90deg, 
        #FF6B35 0%, 
        #FF8C42 25%,
        #FFB347 50%, 
        #D4AF37 75%,
        #B8860B 100%
      );
    }
  `}
  
  ${({ $hover, theme }) => $hover && css`
    cursor: pointer;
    
    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 12px 24px rgba(255, 107, 53, 0.15), ${theme.shadows.lg};
      border-color: rgba(255, 179, 71, 0.4);
      background: linear-gradient(145deg, 
        rgba(255, 255, 255, 1) 0%, 
        rgba(255, 250, 240, 1) 50%,
        rgba(255, 247, 235, 1) 100%
      );
    }
  `}
  
  ${({ $glow }) => $glow && css`
    &:hover::after {
      width: 150px;
      height: 150px;
      background: radial-gradient(circle at 100% 0%, rgba(255, 107, 53, 0.15) 0%, transparent 70%);
    }
  `}
  
  ${({ $clickable }) => $clickable && css`
    cursor: pointer;
  `}
`;

export const Card: React.FC<CardProps> = ({
  children,
  padding = 'md',
  hover = false,
  gradient = false,
  glass = false,
  glow = false,
  onClick,
  className,
  style,
  'data-testid': dataTestId,
}) => {
  return (
    <StyledCard
      $padding={paddingMap[padding]}
      $mobilePadding={mobilePaddingMap[padding]}
      $hover={hover}
      $clickable={!!onClick}
      $gradient={gradient}
      $glass={glass}
      $glow={glow}
      onClick={onClick}
      className={className}
      style={style}
      data-testid={dataTestId}
    >
      {children}
    </StyledCard>
  );
};

// Card subcomponents with enhanced styling
export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid rgba(255, 179, 71, 0.15);
  margin-bottom: ${({ theme }) => theme.spacing.md};
  position: relative;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm};
  
  &::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 60px;
    height: 2px;
    background: linear-gradient(90deg, #FF6B35 0%, #FFB347 100%);
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    padding-bottom: ${({ theme }) => theme.spacing.sm};
    margin-bottom: ${({ theme }) => theme.spacing.sm};
  }
`;

export const CardTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSizes.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  font-family: ${({ theme }) => theme.typography.fontFamilyHeading};
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
  letter-spacing: -0.01em;
  
  /* Optional: Add subtle gold text effect on hover */
  transition: color ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    color: #B8860B;
  }
  
  @media (max-width: 768px) {
    font-size: ${({ theme }) => theme.typography.fontSizes.md};
  }
`;

export const CardContent = styled.div``;

export const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.sm};
  padding-top: ${({ theme }) => theme.spacing.md};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  margin-top: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    padding-top: ${({ theme }) => theme.spacing.sm};
    margin-top: ${({ theme }) => theme.spacing.sm};
    justify-content: stretch;
    
    > * {
      flex: 1;
      min-width: 100px;
    }
  }
`;

// Feature card with icon support
export const FeatureCard = styled(StyledCard)`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xl};
  background: linear-gradient(145deg, 
    rgba(255, 255, 255, 1) 0%, 
    rgba(255, 250, 240, 0.9) 50%,
    rgba(255, 247, 235, 0.8) 100%
  );
  border-color: rgba(255, 179, 71, 0.2);
  
  &::before {
    height: 4px;
    background: linear-gradient(90deg, 
      #FF6B35 0%, 
      #FFB347 50%, 
      #D4AF37 100%
    );
  }
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 16px 32px rgba(255, 107, 53, 0.15), ${({ theme }) => theme.shadows.xl};
    border-color: rgba(255, 179, 71, 0.4);
  }
`;

// Stat card with gradient accent
export const StatCard = styled.div`
  background: linear-gradient(145deg, 
    rgba(255, 255, 255, 1) 0%, 
    rgba(255, 252, 248, 1) 50%,
    rgba(255, 250, 245, 1) 100%
  );
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  transition: all ${({ theme }) => theme.transitions.normal};
  padding: ${({ theme }) => theme.spacing.lg};
  position: relative;
  overflow: hidden;
  
  /* Top gradient accent line */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, 
      #FF6B35 0%, 
      #FF8C42 25%,
      #FFB347 50%, 
      #D4AF37 75%,
      #B8860B 100%
    );
  }
  
  /* Corner accent glow */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 80px;
    height: 80px;
    background: radial-gradient(circle at 100% 0%, rgba(255, 179, 71, 0.1) 0%, transparent 70%);
    pointer-events: none;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(255, 107, 53, 0.12), ${({ theme }) => theme.shadows.md};
    border-color: rgba(255, 179, 71, 0.3);
  }
`;
